import {
  AirItemI,
  CabinI,
  GetBookingMyMscResponse,
  InsuranceItemI,
  ExcursionI,
  MiscellaneousItemI,
  PassengerI,
  TransferI,
} from '@msc-b2c/interfaces/interfaces';
import { BookingStatusEnum } from '@msc-b2c/interfaces/enums';
import { PassengersItemCustomDataI } from '@msc-b2c/interfaces/types';

/**
 * Cabins on the given cruise that have at least one passenger on the booking.
 * Cancelled cabins (and their passengers) are excluded so prepaid drawers only
 * show valid staterooms for multi-cabin bookings.
 */
export function getCabinsWithPassengersForCruise(
  booking: GetBookingMyMscResponse | undefined,
  cruiseId: string | undefined
): CabinI[] {
  if (!booking) {
    return [];
  }
  const bookedCabins = booking.cabins ?? [];
  const scoped = cruiseId
    ? bookedCabins.filter((cabin) => cabin?.cruise?.id === cruiseId)
    : bookedCabins;
  const passengerData = booking.passengers ?? [];
  return scoped.filter((cabin) => {
    if (cabin.bookingStatus?.code === BookingStatusEnum.CANCELLED) {
      return false;
    }
    const cabinPassengerIds = cabin?.passengers ?? [];
    if (!cabin?.id || !Array.isArray(cabinPassengerIds)) {
      return false;
    }
    const cabinPassengerIdList = new Set(
      cabinPassengerIds.map((pax) => (typeof pax === 'object' && pax !== null ? pax.id : pax))
    );
    const matchedPassengers = passengerData.filter(
      (passenger) => passenger?.id && cabinPassengerIdList.has(passenger.id)
    );
    return matchedPassengers.length > 0;
  });
}

/**
 * Secondary ordering for cabins after the active user's cabin.
 */
export function sortCabinsByStateroomNumber(cabins: CabinI[]): CabinI[] {
  return [...cabins].sort((a, b) => {
    const na = a.number ?? '';
    const nb = b.number ?? '';
    const byNumber = na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
    if (byNumber !== 0) {
      return byNumber;
    }
    return (a.id || '').localeCompare(b.id || '');
  });
}

function getCabinLowestPassengerNumber(
  cabin: CabinI,
  passengerNumberById: Map<string, number | null | undefined>
): number | null {
  if (!cabin.id || !cabin.passengers?.length) {
    return null;
  }

  const passengerNumbers = cabin.passengers
    .map((passenger) => passenger.number ?? passengerNumberById.get(passenger.id) ?? null)
    .filter((passengerNumber) => !!passengerNumber);

  if (!passengerNumbers.length) {
    return null;
  }

  return Math.min(...passengerNumbers);
}

/**
 * Orders cabins by the lowest booking passenger number assigned to each cabin.
 */
export function sortCabinsByPassengerOrder(
  cabins: CabinI[],
  booking: GetBookingMyMscResponse | undefined
): CabinI[] {
  const passengerIdToNumber = new Map(
    (booking?.passengers ?? []).map((passenger) => [passenger.id, passenger.number])
  );

  return [...cabins].sort((first, second) => {
    const firstPassengerOrder = getCabinLowestPassengerNumber(first, passengerIdToNumber);
    const secondPassengerOrder = getCabinLowestPassengerNumber(second, passengerIdToNumber);

    if (firstPassengerOrder === null && secondPassengerOrder !== null) {
      return 1;
    }

    if (firstPassengerOrder !== null && secondPassengerOrder === null) {
      return -1;
    }

    if (
      firstPassengerOrder !== null &&
      secondPassengerOrder !== null &&
      firstPassengerOrder !== secondPassengerOrder
    ) {
      return firstPassengerOrder - secondPassengerOrder;
    }

    return (first.id || '').localeCompare(second.id || '');
  });
}

/**
 * My Area passenger UIs: **active user's cabin is always Stateroom 1**; remaining cabins with
 * passengers follow (by cabin number). Passenger rows stay tied to their real `cabin.id`.
 */
export function getOrderedCabinsForPassengerDrawer(
  booking: GetBookingMyMscResponse | undefined,
  cruiseId: string | undefined,
  prioritizedCabinId?: string
): CabinI[] {
  const withPassengers = getCabinsWithPassengersForCruise(booking, cruiseId);
  if (!prioritizedCabinId) {
    return sortCabinsByStateroomNumber(withPassengers);
  }
  const primary = withPassengers.find((c) => c.id === prioritizedCabinId);
  const others = sortCabinsByStateroomNumber(
    withPassengers.filter((c) => c.id !== prioritizedCabinId)
  );
  return primary ? [primary, ...others] : sortCabinsByStateroomNumber(withPassengers);
}

/**
 * Retrieves the list of passengers associated with a specific cabin.
 *
 * @param {string} cabinId - The ID of the cabin.
 * @param {GetBookingMyMscResponse} bookingData - Booking data containing cabins and passengers.
 * @returns {PassengerI[]} The passengers assigned to the specified cabin.
 */
export const getPassengerItemsByCabinId = (
  cabinId: string,
  bookingData: GetBookingMyMscResponse
): PassengerI[] => {
  const cabin: CabinI | undefined = bookingData.cabins.find((c) => c.id === cabinId);
  const passengerIdsInCabin = cabin?.passengers?.map((p) => p.id);
  const passengers: PassengerI[] | undefined = bookingData.passengers?.filter((p) =>
    passengerIdsInCabin?.includes(p.id)
  );
  return passengers || [];
};

export const getPassengersInfoByItem = (
  item: InsuranceItemI | AirItemI | TransferI | ExcursionI | MiscellaneousItemI,
  cabinPassengers: PassengerI[]
): PassengersItemCustomDataI[] => {
  //from array of passengers, filter those who belong to item.passengers
  const itemPassengers = item.passengers || [];
  return cabinPassengers
    .filter((cabinPax) => itemPassengers.some((itemPax) => itemPax.id === cabinPax.id))
    .map((p) => ({
      name: p.name,
      surname: p.surname,
      id: p.id,
      insuranceCode: 'code' in item ? item.code : '',
    }));
};
